import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  ArrowLeft, Search, ShoppingCart, Plus, Minus, X, 
  DollarSign, CreditCard, Trash2, Barcode, Package,
  CheckCircle, AlertCircle, RefreshCw
} from 'lucide-react'
import { api } from '../../../lib/api'
import { useAuth } from '../../../contexts/AuthContext'
import toast from 'react-hot-toast'
import ProductImage from '../../../components/ProductImage'
import { getCacheStale, setCache } from '../../../lib/cache'
import { useScrollHideHeader } from '../hooks/useScrollHideHeader'

interface CartItem {
  productId: string
  variantId?: string
  name: string
  variantName?: string
  price: number
  quantity: number
  imageUrl?: string
  stock: number
  product: any
  variant?: any
}

interface Product {
  id: string
  name: string
  price: number
  stock_quantity?: number
  stockQuantity?: number
  image_url?: string
  imageUrl?: string
  barcode?: string
  has_variants?: boolean
  variants?: Array<{
    id: string
    variant_name: string
    price: number
    stock_quantity?: number
    stockQuantity?: number
    image_url?: string
    imageUrl?: string
  }>
}

const DispatcherPOS = () => {
  const navigate = useNavigate()
  const params = useParams()
  const { user } = useAuth()
  const storeId = params.storeId || user?.storeId
  
  const [products, setProducts] = useState<Product[]>([])
  const [cart, setCart] = useState<CartItem[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'digital_wallet'>('cash')
  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [barcodeInput, setBarcodeInput] = useState('')
  const [barcodeFocus, setBarcodeFocus] = useState(false)
  
  const isHeaderVisible = useScrollHideHeader()
  const barcodeInputRef = useRef<HTMLInputElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (storeId) {
      loadProducts()
    }
  }, [storeId])

  useEffect(() => {
    // Enfocar en barcode input cuando se monta el componente
    if (barcodeInputRef.current && !barcodeFocus) {
      barcodeInputRef.current.focus()
      setBarcodeFocus(true)
    }
  }, [])

  const loadProducts = async () => {
    if (!storeId) return

    const cacheKey = `dispatcher_pos_products_${storeId}`
    const cachedProducts = getCacheStale<Product[]>(cacheKey)
    if (cachedProducts && cachedProducts.length > 0) {
      setProducts(cachedProducts)
      setLoading(false)
    }

    try {
      const response = await api.get(`/products/store/${storeId}/products?limit=500`)
      if (response.data.success) {
        const productsList = (response.data.data || []).map((p: any) => ({
          id: p.id,
          name: p.name,
          price: Number(p.price || 0),
          stock_quantity: Number(p.stock_quantity || p.stockQuantity || 0),
          stockQuantity: Number(p.stock_quantity || p.stockQuantity || 0),
          image_url: p.image_url || p.imageUrl,
          imageUrl: p.image_url || p.imageUrl,
          barcode: p.barcode,
          has_variants: p.has_variants || false,
          variants: (p.variants || []).map((v: any) => ({
            id: v.id,
            variant_name: v.variant_name || v.variantName,
            price: Number(v.price || 0),
            stock_quantity: Number(v.stock_quantity || v.stockQuantity || 0),
            stockQuantity: Number(v.stock_quantity || v.stockQuantity || 0),
            image_url: v.image_url || v.imageUrl,
            imageUrl: v.image_url || v.imageUrl
          }))
        }))
        setProducts(productsList)
        setCache(cacheKey, productsList, 5 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading products:', error)
      toast.error('Error al cargar productos')
    } finally {
      setLoading(false)
    }
  }

  const handleBarcodeScan = async (barcode: string) => {
    if (!barcode || !storeId) return

    try {
      const response = await api.getProductByBarcode(storeId, barcode)
      if (response.success && response.data) {
        const product = response.data
        addToCart(product)
        setBarcodeInput('')
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus()
        }
      } else {
        toast.error('Producto no encontrado')
        setBarcodeInput('')
      }
    } catch (error: any) {
      console.error('Error scanning barcode:', error)
      toast.error('Error al buscar producto')
      setBarcodeInput('')
    }
  }

  const handleBarcodeKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && barcodeInput.trim()) {
      handleBarcodeScan(barcodeInput.trim())
    }
  }

  const addToCart = (product: any, variant?: any) => {
    const productId = product.id
    const variantId = variant?.id
    const price = variant ? Number(variant.price || 0) : Number(product.price || 0)
    const stock = variant 
      ? Number(variant.stock_quantity || variant.stockQuantity || 0)
      : Number(product.stock_quantity || product.stockQuantity || 0)
    const name = product.name
    const variantName = variant?.variant_name || variant?.variantName
    const imageUrl = variant?.image_url || variant?.imageUrl || product.image_url || product.imageUrl

    if (stock <= 0) {
      toast.error('Producto sin stock disponible')
      return
    }

    setCart(prev => {
      const existingItem = prev.find(
        item => item.productId === productId && item.variantId === variantId
      )

      if (existingItem) {
        if (existingItem.quantity >= stock) {
          toast.error('No hay suficiente stock disponible')
          return prev
        }
        return prev.map(item =>
          item.productId === productId && item.variantId === variantId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      }

      return [
        ...prev,
        {
          productId,
          variantId,
          name,
          variantName,
          price,
          quantity: 1,
          imageUrl,
          stock,
          product,
          variant
        }
      ]
    })
  }

  const updateCartQuantity = (productId: string, variantId: string | undefined, delta: number) => {
    setCart(prev =>
      prev.map(item => {
        if (item.productId === productId && item.variantId === variantId) {
          const newQuantity = item.quantity + delta
          if (newQuantity <= 0) {
            return null as any
          }
          if (newQuantity > item.stock) {
            toast.error('No hay suficiente stock disponible')
            return item
          }
          return { ...item, quantity: newQuantity }
        }
        return item
      }).filter(Boolean)
    )
  }

  const removeFromCart = (productId: string, variantId: string | undefined) => {
    setCart(prev =>
      prev.filter(
        item => !(item.productId === productId && item.variantId === variantId)
      )
    )
  }

  const clearCart = () => {
    setCart([])
    setCustomerName('')
    setCustomerPhone('')
  }

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0)
  }

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío')
      return
    }

    if (!customerName.trim()) {
      toast.error('Ingresa el nombre del cliente')
      return
    }

    if (!storeId) {
      toast.error('Negocio no válido')
      return
    }

    setProcessing(true)
    try {
      // Crear orden con status "delivered" directamente (venta física)
      const orderData = {
        storeId,
        items: cart.map(item => ({
          productId: item.productId,
          variantId: item.variantId,
          quantity: item.quantity
        })),
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim() || undefined,
        paymentMethod,
        status: 'delivered', // Venta física completada inmediatamente
        deliveryAddress: 'Venta en local', // Indicador de venta física
        notes: 'Venta realizada en punto de venta físico'
      }

      const response = await api.createOrder(orderData)
      
      if (response.success) {
        toast.success('Venta realizada exitosamente')
        
        // Limpiar carrito y formulario
        clearCart()
        
        // Recargar productos para actualizar stock
        await loadProducts()
        
        // Enfocar en barcode input para siguiente venta
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus()
        }
      } else {
        toast.error(response.message || 'Error al procesar la venta')
      }
    } catch (error: any) {
      console.error('Error processing sale:', error)
      toast.error(error.response?.data?.message || 'Error al procesar la venta')
    } finally {
      setProcessing(false)
    }
  }

  const filteredProducts = products.filter(product => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      product.name.toLowerCase().includes(search) ||
      product.barcode?.toLowerCase().includes(search) ||
      (product.variants || []).some((v: any) => 
        v.variant_name?.toLowerCase().includes(search)
      )
    )
  })

  const availableProducts = filteredProducts.filter(p => {
    if (p.has_variants && p.variants && p.variants.length > 0) {
      return p.variants.some((v: any) => (v.stock_quantity || v.stockQuantity || 0) > 0)
    }
    return (p.stock_quantity || p.stockQuantity || 0) > 0
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-600" />
          <p className="text-gray-700 font-light">Cargando productos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-16 pb-20">
      {/* Header */}
      <div className={`fixed top-16 left-0 right-0 z-[9998] bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-lg shadow-black/5 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      } lg:translate-y-0`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dispatcher')}
              className="p-2 sm:p-2.5 hover:bg-gray-100 rounded-xl sm:rounded-2xl transition-colors active:scale-95"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-light text-gray-900 truncate">Punto de Venta</h1>
              <p className="text-sm text-gray-500 truncate">Ventas físicas en local</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 pt-20 sm:pt-24 lg:pt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Panel izquierdo - Productos */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Barcode Scanner */}
            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <Barcode className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                <input
                  ref={barcodeInputRef}
                  type="text"
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  onKeyPress={handleBarcodeKeyPress}
                  placeholder="Escanear código de barras..."
                  className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200/50 rounded-xl sm:rounded-2xl focus:border-gray-300 focus:bg-white/80 focus:outline-none text-sm sm:text-base"
                />
                <button
                  onClick={() => handleBarcodeScan(barcodeInput.trim())}
                  disabled={!barcodeInput.trim()}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-xl sm:rounded-2xl shadow-md shadow-gray-900/20 hover:shadow-lg hover:shadow-gray-900/30 transition-all duration-200 disabled:opacity-50 font-medium text-xs sm:text-sm active:scale-95"
                >
                  Buscar
                </button>
              </div>
            </div>

            {/* Búsqueda */}
            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Buscar productos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 sm:py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200/50 rounded-xl sm:rounded-2xl focus:border-gray-300 focus:bg-white/80 focus:outline-none text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Lista de productos */}
            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4">
              <h2 className="text-base sm:text-lg font-light text-gray-900 mb-3 sm:mb-4">
                Productos Disponibles ({availableProducts.length})
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 max-h-[60vh] overflow-y-auto">
                {availableProducts.map(product => {
                  if (product.has_variants && product.variants && product.variants.length > 0) {
                    return product.variants.map((variant: any) => {
                      const stock = variant.stock_quantity || variant.stockQuantity || 0
                      if (stock <= 0) return null
                      return (
                        <button
                          key={`${product.id}-${variant.id}`}
                          onClick={() => addToCart(product, variant)}
                          className="p-2 sm:p-3 bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-200/50 hover:bg-white/70 hover:shadow-md transition-all duration-200 active:scale-95 text-left"
                        >
                          <div className="aspect-square w-full mb-2 rounded-lg sm:rounded-xl overflow-hidden bg-gray-100">
                            <ProductImage
                              imageKey={variant.image_url || variant.imageUrl || product.image_url || product.imageUrl}
                              alt={variant.variant_name || variant.variantName}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="text-[10px] sm:text-xs font-medium text-gray-900 truncate mb-1">
                            {product.name}
                          </p>
                          <p className="text-[9px] sm:text-[10px] text-gray-600 truncate mb-1">
                            {variant.variant_name || variant.variantName}
                          </p>
                          <div className="flex items-center justify-between">
                            <p className="text-xs sm:text-sm font-semibold text-gray-900">
                              RD${Number(variant.price || 0).toFixed(2)}
                            </p>
                            <span className="text-[9px] sm:text-[10px] text-gray-500">
                              Stock: {stock}
                            </span>
                          </div>
                        </button>
                      )
                    })
                  } else {
                    const stock = product.stock_quantity || product.stockQuantity || 0
                    if (stock <= 0) return null
                    return (
                      <button
                        key={product.id}
                        onClick={() => addToCart(product)}
                        className="p-2 sm:p-3 bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-200/50 hover:bg-white/70 hover:shadow-md transition-all duration-200 active:scale-95 text-left"
                      >
                        <div className="aspect-square w-full mb-2 rounded-lg sm:rounded-xl overflow-hidden bg-gray-100">
                          <ProductImage
                            imageKey={product.image_url || product.imageUrl}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <p className="text-[10px] sm:text-xs font-medium text-gray-900 truncate mb-1">
                          {product.name}
                        </p>
                        <div className="flex items-center justify-between">
                          <p className="text-xs sm:text-sm font-semibold text-gray-900">
                            RD${Number(product.price || 0).toFixed(2)}
                          </p>
                          <span className="text-[9px] sm:text-[10px] text-gray-500">
                            Stock: {stock}
                          </span>
                        </div>
                      </button>
                    )
                  }
                })}
              </div>
              {availableProducts.length === 0 && (
                <div className="text-center py-8 sm:py-12">
                  <Package className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 text-sm sm:text-base">No hay productos disponibles</p>
                </div>
              )}
            </div>
          </div>

          {/* Panel derecho - Carrito */}
          <div className="lg:col-span-1">
            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-3 sm:p-4 sticky top-24">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-base sm:text-lg font-light text-gray-900 flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6" />
                  Carrito ({cart.length})
                </h2>
                {cart.length > 0 && (
                  <button
                    onClick={clearCart}
                    className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-xl transition-colors active:scale-95"
                  >
                    <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  </button>
                )}
              </div>

              {/* Items del carrito */}
              <div className="space-y-2 sm:space-y-3 max-h-[40vh] overflow-y-auto mb-4">
                {cart.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm">El carrito está vacío</p>
                  </div>
                ) : (
                  cart.map((item) => (
                    <div
                      key={`${item.productId}-${item.variantId || 'none'}`}
                      className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-200/50"
                    >
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                        <ProductImage
                          imageKey={item.imageUrl}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                          {item.name}
                        </p>
                        {item.variantName && (
                          <p className="text-[10px] sm:text-xs text-gray-600 truncate">
                            {item.variantName}
                          </p>
                        )}
                        <p className="text-xs sm:text-sm font-semibold text-gray-900">
                          RD${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <button
                          onClick={() => updateCartQuantity(item.productId, item.variantId, -1)}
                          className="p-1 sm:p-1.5 hover:bg-gray-100 rounded-lg transition-colors active:scale-95"
                        >
                          <Minus className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                        </button>
                        <span className="text-xs sm:text-sm font-medium text-gray-900 w-6 sm:w-8 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateCartQuantity(item.productId, item.variantId, 1)}
                          disabled={item.quantity >= item.stock}
                          className="p-1 sm:p-1.5 hover:bg-gray-100 rounded-lg transition-colors active:scale-95 disabled:opacity-50"
                        >
                          <Plus className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.productId, item.variantId)}
                          className="p-1 sm:p-1.5 hover:bg-red-100 rounded-lg transition-colors active:scale-95 ml-1"
                        >
                          <X className="w-3 h-3 sm:w-4 sm:h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Información del cliente */}
              {cart.length > 0 && (
                <div className="space-y-3 sm:space-y-4 mb-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                      Nombre del Cliente *
                    </label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Nombre completo"
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200/50 rounded-xl sm:rounded-2xl focus:border-gray-300 focus:bg-white/80 focus:outline-none text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                      Teléfono (opcional)
                    </label>
                    <input
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      placeholder="809-000-0000"
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200/50 rounded-xl sm:rounded-2xl focus:border-gray-300 focus:bg-white/80 focus:outline-none text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                      Método de Pago
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {(['cash', 'card', 'digital_wallet'] as const).map((method) => (
                        <button
                          key={method}
                          onClick={() => setPaymentMethod(method)}
                          className={`p-2 sm:p-3 rounded-xl sm:rounded-2xl border-2 transition-all duration-200 active:scale-95 ${
                            paymentMethod === method
                              ? 'border-gray-700 bg-gray-700 text-white'
                              : 'border-gray-200 bg-white/60 text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          {method === 'cash' && <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1" />}
                          {method === 'card' && <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1" />}
                          {method === 'digital_wallet' && <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1" />}
                          <span className="text-[9px] sm:text-xs font-medium block">
                            {method === 'cash' ? 'Efectivo' : method === 'card' ? 'Tarjeta' : 'Digital'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Total y botón de pago */}
              {cart.length > 0 && (
                <div className="border-t border-gray-200/50 pt-3 sm:pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm sm:text-base font-medium text-gray-700">Total:</span>
                    <span className="text-lg sm:text-xl font-bold text-gray-900">
                      RD${calculateTotal().toFixed(2)}
                    </span>
                  </div>
                  <button
                    onClick={handleCheckout}
                    disabled={processing || !customerName.trim()}
                    className="w-full py-3 sm:py-4 bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-xl sm:rounded-2xl shadow-md shadow-gray-900/20 hover:shadow-lg hover:shadow-gray-900/30 transition-all duration-200 disabled:opacity-50 font-medium text-sm sm:text-base flex items-center justify-center gap-2 active:scale-95"
                  >
                    {processing ? (
                      <>
                        <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                        <span>Procesando...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>Completar Venta</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DispatcherPOS

